import { useEffect, useMemo, useState } from "react";
import { Info } from "lucide-react";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { detailedProfit } from "../lib/calcEngine";
import { formatCurrency, formatPct } from "../lib/format";
import { api } from "../lib/api";
import { useSeo, useJsonLd, SITE_URL } from "../lib/seo";
import { track } from "../lib/analytics";

const PLATFORMS = [
  { key: "meesho", label: "Meesho", def: { commissionPct: 5, shippingCost: 70, gstPct: 18, fixedFee: 0, returnRatePct: 15 } },
  { key: "amazon", label: "Amazon", def: { commissionPct: 15, shippingCost: 70, gstPct: 18, fixedFee: 30, returnRatePct: 5 } },
  { key: "flipkart", label: "Flipkart", def: { commissionPct: 12, shippingCost: 70, gstPct: 18, fixedFee: 25, returnRatePct: 8 } },
];

const numField = (v) => parseFloat(v) || 0;

export default function Compare() {
  const [shared, setShared] = useState({ productCost: "400", sellingPrice: "999", adCost: "60" });
  const [assumptions, setAssumptions] = useState(
    Object.fromEntries(PLATFORMS.map((p) => [p.key, { ...p.def }]))
  );
  const [updatedAt, setUpdatedAt] = useState("");

  useSeo({
    title: "Compare Marketplace Profit — Meesho vs Amazon vs Flipkart | ProfitMigo",
    description: "Compare true profit and margin for the same product across Meesho, Amazon India and Flipkart, using one engine and configurable fee assumptions.",
    path: "/compare",
  });
  useJsonLd("ld-compare", {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL + "/" },
      { "@type": "ListItem", position: 2, name: "Compare marketplaces", item: SITE_URL + "/compare" },
    ],
  });

  useEffect(() => {
    track("page_view", { path: "/compare", calculator: "compare" });
    api.get("/fees").then(({ data }) => {
      if (data?.length) setUpdatedAt(data[0].updated_at || "");
    }).catch(() => {});
  }, []);

  const results = useMemo(() => {
    const s = { productCost: numField(shared.productCost), sellingPrice: numField(shared.sellingPrice), adCost: numField(shared.adCost) };
    return PLATFORMS.map((p) => {
      const a = assumptions[p.key];
      const r = detailedProfit({
        sellingPrice: s.sellingPrice,
        productCost: s.productCost,
        adCost: s.adCost,
        commissionPct: numField(a.commissionPct),
        shippingCost: numField(a.shippingCost),
        gstPct: numField(a.gstPct),
        fixedFee: numField(a.fixedFee),
        returnRatePct: numField(a.returnRatePct),
      });
      return { platform: p, r, shipping: numField(a.shippingCost) };
    });
  }, [shared, assumptions]);

  const bestProfit = Math.max(...results.map((x) => x.r.netProfit));

  const setAssumption = (pk, key, val) =>
    setAssumptions((prev) => ({ ...prev, [pk]: { ...prev[pk], [key]: val } }));

  const rows = [
    { label: "Marketplace fees", get: (x) => formatCurrency(x.r.feeCosts) },
    { label: "Shipping", get: (x) => formatCurrency(x.shipping) },
    { label: "Advertising", get: () => formatCurrency(numField(shared.adCost)) },
    { label: "Est. return cost", get: (x) => formatCurrency(x.r.expectedReturnCost) },
  ];

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Compare marketplaces" }]} />
      <header className="max-w-3xl">
        <h1 className="font-display text-4xl font-bold tracking-tight sm:text-5xl">Compare marketplace profit</h1>
        <p className="mt-4 text-lg text-muted-foreground">
          See the true profit and margin for the same product across Meesho, Amazon India and Flipkart — one engine, your own fee assumptions.
        </p>
      </header>

      {/* Shared product inputs */}
      <div className="mt-8 grid grid-cols-1 gap-4 rounded-xl border border-border bg-card p-6 sm:grid-cols-3">
        {[
          { key: "sellingPrice", label: "Selling price" },
          { key: "productCost", label: "Product cost" },
          { key: "adCost", label: "Advertising cost" },
        ].map((fld) => (
          <div key={fld.key} className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{fld.label}</Label>
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">₹</span>
              <Input
                type="number"
                data-testid={`compare-${fld.key}`}
                className="tabular h-12 pl-7"
                value={shared[fld.key]}
                onChange={(e) => setShared((p) => ({ ...p, [fld.key]: e.target.value }))}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Comparison table */}
      <div className="mt-6 overflow-x-auto rounded-xl border border-border bg-card" data-testid="compare-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left">
              <th className="px-5 py-4 text-xs uppercase tracking-wide text-muted-foreground">Per order</th>
              {results.map((x) => (
                <th key={x.platform.key} className="px-5 py-4 text-right font-display text-base font-semibold">{x.platform.label}</th>
              ))}
            </tr>
          </thead>
          <tbody className="tabular">
            {rows.map((row) => (
              <tr key={row.label} className="border-b border-border/60">
                <td className="px-5 py-3 text-muted-foreground">{row.label}</td>
                {results.map((x) => (
                  <td key={x.platform.key} className="px-5 py-3 text-right font-mono">{row.get(x)}</td>
                ))}
              </tr>
            ))}
            <tr className="border-b border-border bg-slate-900 text-white">
              <td className="px-5 py-4 font-semibold">TRUE PROFIT</td>
              {results.map((x) => {
                const best = x.r.netProfit === bestProfit && bestProfit > 0;
                return (
                  <td key={x.platform.key} className={`px-5 py-4 text-right font-mono text-lg font-bold ${x.r.netProfit < 0 ? "text-red-400" : best ? "text-emerald-400" : "text-white"}`} data-testid={`compare-profit-${x.platform.key}`}>
                    {formatCurrency(x.r.netProfit)} {best && <span className="ml-1 text-[10px] uppercase text-emerald-400">best</span>}
                  </td>
                );
              })}
            </tr>
            <tr>
              <td className="px-5 py-3 font-semibold">Margin</td>
              {results.map((x) => (
                <td key={x.platform.key} className={`px-5 py-3 text-right font-mono ${x.r.marginPct < 0 ? "text-red-600" : "text-emerald-600"}`} data-testid={`compare-margin-${x.platform.key}`}>
                  {formatPct(x.r.marginPct)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      <p className="mt-4 flex items-start gap-2 rounded-md bg-secondary p-3 text-xs text-muted-foreground">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>Fee assumptions are editable estimates{updatedAt ? `, last reviewed ${updatedAt}` : ""}. They are not official marketplace facts — commission, shipping and fixed fees vary by category and change over time. Adjust them below for your product.</span>
      </p>

      {/* Editable per-platform assumptions */}
      <div className="mt-6">
        <Accordion type="single" collapsible className="w-full">
          {PLATFORMS.map((p) => (
            <AccordionItem key={p.key} value={p.key}>
              <AccordionTrigger data-testid={`compare-assumptions-${p.key}`}>Edit {p.label} fee assumptions</AccordionTrigger>
              <AccordionContent>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
                  {[
                    { k: "commissionPct", l: "Commission %" },
                    { k: "fixedFee", l: "Fixed fee ₹" },
                    { k: "shippingCost", l: "Shipping ₹" },
                    { k: "gstPct", l: "GST %" },
                    { k: "returnRatePct", l: "Return %" },
                  ].map((fld) => (
                    <div key={fld.k} className="space-y-1">
                      <Label className="text-xs">{fld.l}</Label>
                      <Input
                        type="number"
                        className="tabular"
                        data-testid={`compare-${p.key}-${fld.k}`}
                        value={assumptions[p.key][fld.k]}
                        onChange={(e) => setAssumption(p.key, fld.k, e.target.value)}
                      />
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  );
}
